import { useState, useCallback, useEffect } from 'react'
import {
  type ODataQueryParams,
  type FilterCondition,
  type TableState,
  defaultTableState,
  tableStateToODataParams,
} from '@/lib/api/odata'

interface UseDataTableOptions {
  initialState?: Partial<TableState>
  onStateChange?: (params: ODataQueryParams) => void
}

export function useDataTable(options: UseDataTableOptions = {}) {
  const { initialState, onStateChange } = options

  const [state, setState] = useState<TableState>({
    ...defaultTableState,
    ...initialState,
  })

  // Convert state to OData params whenever state changes
  useEffect(() => {
    if (onStateChange) {
      const params = tableStateToODataParams(state)
      onStateChange(params)
    }
  }, [state, onStateChange])

  const setPage = useCallback((page: number) => {
    setState((prev) => ({ ...prev, page }))
  }, [])

  const setPageSize = useCallback((pageSize: number) => {
    setState((prev) => ({ ...prev, pageSize, page: 1 }))
  }, [])

  const setSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setState((prev) => ({
      ...prev,
      sortField: field,
      sortDirection: direction,
    }))
  }, [])

  const setFilters = useCallback((filters: Record<string, FilterCondition>) => {
    setState((prev) => ({ ...prev, filters, page: 1 }))
  }, [])

  const setSearch = useCallback((search: string) => {
    setState((prev) => ({ ...prev, search, page: 1 }))
  }, [])

  const reset = useCallback(() => {
    setState({ ...defaultTableState, ...initialState })
  }, [initialState])

  const getODataParams = useCallback((): ODataQueryParams => {
    return tableStateToODataParams(state)
  }, [state])

  return {
    // State
    state,
    page: state.page,
    pageSize: state.pageSize,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
    filters: state.filters,
    search: state.search,

    // Actions
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    reset,

    // Utils
    getODataParams,
  }
}
